import { useState } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * <EmailLink>
 *
 * The official Gomes Digital Lab email touch-point. Used everywhere a visitor
 * or client needs to reach the studio. One component → one consistent
 * branding rule across public site, portal, and admin.
 *
 * Behaviour:
 *   - Primary click → opens the system mail composer (mailto:)
 *   - Adjacent copy icon → copies the address to the clipboard with a brief
 *     "Copied" check-mark animation and a sonner toast confirmation
 *   - Subtle gold underline + arrow micro-animation on hover
 *
 * Variants:
 *   • "inline"  — text-style link (footer, contact section, body copy)
 *   • "card"    — boxed pill for primary CTAs and the portal sidebar
 *   • "mono"    — small mono-font badge for the application success state
 *
 * Theme:
 *   • "light" (default) — for ivory backgrounds (public + portal)
 *   • "dark" — for the admin onyx UI
 */
export default function EmailLink({
  email,
  label,
  subject = "",
  body = "",
  variant = "inline",
  theme = "light",
  iconOnly = false,
  className = "",
  testid,
}) {
  const [copied, setCopied] = useState(false);
  if (!email) return null;

  const mailto = `mailto:${email}` + (
    subject || body
      ? `?${[
          subject && `subject=${encodeURIComponent(subject)}`,
          body && `body=${encodeURIComponent(body)}`,
        ].filter(Boolean).join("&")}`
      : ""
  );

  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success(`${email} copied to clipboard.`);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy. Please copy the address manually.");
    }
  };

  const palette = theme === "dark"
    ? {
        text: "text-foreground/80",
        textHover: "hover:text-gold",
        icon: "text-gold/70 group-hover:text-gold",
        border: "border-onyx-border",
        borderHover: "hover:border-gold/60",
        bg: "bg-onyx-surface",
        copy: "text-foreground/40 hover:text-gold",
      }
    : {
        text: "text-graphite",
        textHover: "hover:text-ink",
        icon: "text-gold-light group-hover:text-gold-deep",
        border: "border-line",
        borderHover: "hover:border-gold-light",
        bg: "bg-paper",
        copy: "text-mute hover:text-gold-deep",
      };

  if (variant === "card") {
    return (
      <div
        className={`group inline-flex items-stretch overflow-hidden border ${palette.border} ${palette.borderHover} ${palette.bg} transition-colors ${className}`}
        data-testid={testid}
      >
        <a
          href={mailto}
          className={`flex items-center gap-3 px-5 py-3 ${palette.text} ${palette.textHover} transition-colors`}
        >
          <Mail size={14} className={`${palette.icon} transition-colors`} />
          <span className="flex flex-col leading-tight">
            {label && (
              <span className="text-[0.55rem] uppercase tracking-[0.22em] opacity-60">{label}</span>
            )}
            <span className="text-sm tracking-wide">{email}</span>
          </span>
        </a>
        <button
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${email}`}
          className={`px-3 border-l ${palette.border} ${palette.copy} transition-colors`}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
    );
  }

  if (variant === "mono") {
    return (
      <span
        className={`group inline-flex items-center gap-2 font-mono text-[0.78rem] ${palette.text} ${className}`}
        data-testid={testid}
      >
        <a href={mailto} className={`${palette.textHover} transition-colors underline-offset-4 hover:underline`}>
          {email}
        </a>
        <button
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${email}`}
          className={`${palette.copy} transition-colors`}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
        </button>
      </span>
    );
  }

  // Default: "inline" variant
  return (
    <span className={`group inline-flex items-center gap-2 ${className}`} data-testid={testid}>
      <a
        href={mailto}
        className={`inline-flex items-center gap-2 ${palette.text} ${palette.textHover} transition-colors`}
      >
        <Mail
          size={13}
          className={`${palette.icon} transition-transform duration-300 group-hover:-translate-y-px`}
        />
        {!iconOnly && (
          <span className="relative text-sm">
            {email}
            {/* Animated gold underline on hover (last line of brand polish). */}
            <span
              className={`absolute left-0 -bottom-0.5 h-px w-0 bg-gold-light group-hover:w-full transition-all duration-500`}
            />
          </span>
        )}
      </a>
      {!iconOnly && (
        <button
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${email}`}
          className={`${palette.copy} transition-colors opacity-0 group-hover:opacity-100`}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      )}
    </span>
  );
}
